import { observer } from 'mobx-react-lite'
import React, { FocusEventHandler, useEffect, useRef } from 'react'
import { useTreeState } from '../../../stores/Root'
import { STRETCH_RANGE } from '../../../stores/song/clip/ClipStore'
import { CaretHorizontal } from '../../core/icons'
import { DragWrapper, Input, InputContainer, Label, TempoContainer } from './common'
import MatchBPMButton from './MatchBPMButton'

const DRAG_MULTIPLIER = 0.02

interface Props {
  value: number
  onChange: (newValue: number) => void
  showMatchButton?: boolean
  onMatchClick?: () => void
}

const clamp = (newValue: number): number => {
  return Math.max(Math.min(newValue, STRETCH_RANGE[1]), STRETCH_RANGE[0])
}

const useDragInput = (value: number, onChange: (newValue: number) => void) => {
  const { studio } = useTreeState()
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const element = ref.current
    let initialX = 0,
      initialValue = value

    const handleDragStart = (evt: MouseEvent) => {
      initialX = evt.clientX
      initialValue = value
      studio.disableAudioUpdates()
      document.addEventListener('mousemove', handleDragMove)
      document.addEventListener('mouseup', handleDragEnd)
      document.body.classList.add('resize')
    }

    const handleDragEnd = () => {
      initialX = 0
      document.removeEventListener('mousemove', handleDragMove)
      document.removeEventListener('mouseup', handleDragEnd)
      studio.enableAudioUpdates()
      document.body.classList.remove('resize')
    }

    const handleDragMove = (evt: MouseEvent) => {
      const deltaX = evt.clientX - initialX
      onChange(clamp(deltaX * DRAG_MULTIPLIER + initialValue))
    }

    element.addEventListener('mousedown', handleDragStart)

    return () => element.removeEventListener('mousedown', handleDragStart)
  }, [ref.current, onChange, value])
  return ref
}

const Stretch: React.FC<Props> = ({ value, onChange, showMatchButton, onMatchClick }) => {
  const stretchRef = React.createRef<HTMLInputElement>()

  const updateStretch = (stretch: string, onChange: (newValue: number) => void) => {
    stretch = stretch.replace('x', '')
    let newStretch: number
    try {
      newStretch = Number(stretch)
    } catch {
      stretchRef.current.value = value.toString()
      return
    }
    newStretch = clamp(newStretch)
    onChange(newStretch)
  }

  const dragRef = useDragInput(value, onChange)

  React.useEffect(() => {
    if (stretchRef.current) stretchRef.current.value = `${value.toFixed(2).toString()}x`
  }, [stretchRef.current, value])

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) =>
    updateStretch(e.target.value, onChange)

  return (
    <TempoContainer>
      <Label>Stretch</Label>
      <InputContainer>
        {showMatchButton && <MatchBPMButton onClick={onMatchClick} />}
        <Input ref={stretchRef} onBlur={handleBlur} css={{ width: '48px', maxWidth: '48px' }} />
        <DragWrapper ref={dragRef}>{<CaretHorizontal width={'18'} height={'18'} />}</DragWrapper>
      </InputContainer>
    </TempoContainer>
  )
}

export default observer(Stretch)
